import React, { useEffect, useRef, useState } from 'react';
// @ts-ignore
import Timer from 'easytimer.js';
import Daily, {
  DailyCall,
  DailyEventObjectParticipant,
} from '@daily-co/daily-js';
import { Slide, Snackbar } from '@material-ui/core';
import * as lib from '~/dependencies';
import { electron } from '~/helpers';
import { useQuery } from '~/hooks';
import { exitTone } from '~/assets/sounds';
import ScreenShare from '~/assets/images/screen_share.png';
import StopScreenShare from '~/assets/images/stop_screen_share.png';
import { ScreenShareSelector } from './components';
import { BetaMessage, GenericMessage } from './styles';

export enum CallSessionState {
  INITIAL = 'INITIAL',
  ESTABLISHING = 'ESTABLISHING',
  ESTABLISHED = 'ESTABLISHED',
  DISCONNECTED = 'DISCONNECTED',
  FAILED = 'FAILED',
  TERMINATED = 'TERMINATED',
}

export type ScreenShareDeviceType = {
  id: string;
  name: string;
  thumbnail: string;
};

export default function Video() {
  // REF's
  const broadcastChannel = useRef<BroadcastChannel | null>(null);
  const timer = useRef<Timer | null>(null);
  const cameraTimer = useRef<Timer | null>(null);
  const callObject = useRef<DailyCall | null>(null);
  const audioPlayer = useRef<HTMLAudioElement>(null);

  // STATES
  const [genericMessage, setGenericMessage] = useState<string | null>(null);
  const [openMessage, setOpenMessage] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isLocalCameraOn, setIsLocalCameraOn] = useState<boolean>(true);
  const [isLocalScreenOn, setIsLocalScreenOn] = useState<boolean>(false);
  const [isRemoteCameraOn, setIsRemoteCameraOn] = useState<boolean>(true);
  const [isRemoteScreenOn, setIsRemoteScreenOn] = useState<boolean>(false);
  const [openScreenSelector, setOpenScreenSelector] = useState<boolean>(false);
  const [screenDevices, setScreenDevices] = useState<ScreenShareDeviceType[]>(
    []
  );

  // HOOKS
  const queryParam = useQuery();
  const { t } = lib.useTranslation();

  // CONST'S
  const callId = queryParam.get('callId');
  const from = queryParam.get('from');
  const fromName = queryParam.get('fromName');
  const to = queryParam.get('to');
  const toName = queryParam.get('toName');
  const isInviter = queryParam.get('isInviter') === 'true';
  const callRoomUrl = queryParam.get('roomUrl');
  const localUserName = isInviter ? fromName : toName;
  const remoteUserName = isInviter ? toName : fromName;
  const noAnswerTimeout = 60000;
  const disableCamerasTimeout = 120000;

  const sendHangup = () => {
    if (!broadcastChannel.current) return;

    broadcastChannel.current.postMessage({
      type: 'hangup',
      callId,
      from: isInviter ? from : to,
      to: isInviter ? to : from,
    });
  };

  const handleHangup = () => {
    setTimeout(() => {
      sendHangup();
      electron.endVideoCall();
      electron.changePhoneStatus('Available', false);
    }, 10000);
  };

  const handleReject = () => {
    displayMessage(t('screens.video.busyMsg', { remoteUserName }));

    setTimeout(() => {
      electron.endVideoCall();
      electron.changePhoneStatus('Available', false);
      sendHangup();
    }, 10000);
  };

  const sendCallStatus = () => {
    if (!broadcastChannel.current) return;

    broadcastChannel.current.postMessage({
      type: 'callStatus',
    });
  };

  const displayMessage = (message: string) => {
    setGenericMessage(message);
    setOpenMessage(true);
  };

  const sortWindowFirst = (arr: ScreenShareDeviceType[]) => {
    return arr.sort((a, b) => {
      const isAWindow = a.id.startsWith('screen:');
      const isBWindow = b.id.startsWith('screen:');

      if (isAWindow && !isBWindow) return -1;
      if (!isAWindow && isBWindow) return 1;

      return 0;
    });
  };

  const isScreenShareEnabled = () => {
    if (!callObject.current) return false;

    const participants = callObject.current.participants();
    const { local } = participants;

    return !!(local?.tracks?.screenVideo?.state === 'playable');
  };

  const listenCallEvents = (call: DailyCall) => {
    call.on('joined-meeting', () => {
      setIsConnected(true);
      sendCallStatus();

      // Change the electron badge status
      electron.changePhoneStatus('Available', true);

      call.setTheme({
        colors: {
          mainAreaBg: '#364150', // Background color for the main call area.
          mainAreaBgAccent: '#444746', // Background color for video tiles.
          background: '#FFFFFF', // Top bar and call control
        },
      });
    });

    call.on('left-meeting', () => {
      setIsConnected(false);

      electron.changePhoneStatus('Available', false);

      // Hide the Daily screen as soon as possible
      // @ts-ignore
      call.iframe().style.display = 'none';

      sendHangup();
      electron.endVideoCall();
    });

    call.on('participant-updated', (event: DailyEventObjectParticipant) => {
      const { local, tracks } = event.participant;
      const isCameraOn = tracks.video.state === 'playable';
      const isScreenOn = tracks.screenVideo.state === 'playable';

      if (local) {
        setIsLocalCameraOn(isCameraOn);
        setIsLocalScreenOn(isScreenOn);
      } else {
        setIsRemoteCameraOn(isCameraOn);
        setIsRemoteScreenOn(isScreenOn);
      }
    });

    call.on('participant-left', () => {
      displayMessage(t('screens.video.userLeft', { remoteUserName }));

      // Play the exit tone
      if (audioPlayer.current) {
        audioPlayer.current.play();
      }

      handleHangup();
    });

    call.on('local-screen-share-started', () => {
      call.updateCustomTrayButtons({
        shareScreen: {
          iconPath: StopScreenShare,
          label: 'Stop',
          tooltip: 'Stop Sharing',
          visualState: 'active',
        },
      });
    });

    call.on('local-screen-share-stopped', () => {
      call.updateCustomTrayButtons({
        shareScreen: {
          iconPath: ScreenShare,
          label: 'Share',
          tooltip: 'Share Screen',
        },
      });
    });

    call.on('custom-button-click', async ev => {
      if (ev.button_id === 'shareScreen') {
        const isSharingEnabled = isScreenShareEnabled();
        const screenPermission = await electron.checkScreenPermission();

        if (screenPermission !== 'granted') return;

        if (isSharingEnabled) {
          call.stopScreenShare();

          return;
        }

        const screens = await electron.requestScreenShare();
        setScreenDevices(sortWindowFirst(screens));
        setOpenScreenSelector(true);
      }
    });
  };

  const handleScreenShareSelection = (screenId: string) => {
    if (!callObject.current) return;

    callObject.current.startScreenShare({
      displayMediaOptions: {
        video: true,
        audio: false,
        chromeMediaSourceId: screenId,
      },
    });

    setOpenScreenSelector(false);
  };

  const createVideoFrame = async (roomUrl: any) => {
    if (!roomUrl) return;

    const call = Daily.createFrame({
      showLeaveButton: true,
      showParticipantsBar: false,
      showUserNameChangeUI: false,
      iframeStyle: {
        position: 'fixed',
        width: '100%',
        height: '100%',
      },
      cssText: `.screen-share-controls { display: none !important; }`,
      customTrayButtons: {
        shareScreen: {
          iconPath: ScreenShare,
          label: 'Share',
          tooltip: 'Share Screen',
        },
      },
    });

    call.join({
      url: roomUrl,
      userName: localUserName || 'Anonymous',
      showParticipantsBar: false,
    });

    listenCallEvents(call);

    callObject.current = call;
  };

  const startCall = () => {
    if (callRoomUrl) {
      createVideoFrame(callRoomUrl);
    }
  };

  const handleBroadcastChannelMessages = (ev: MessageEvent) => {
    const event = ev.data as any;

    switch (event.type) {
      case 'reject':
        handleReject();
        break;
      default:
    }
  };

  const startTimers = () => {
    timer.current = new Timer();
    timer.current.start();
    timer.current.addEventListener('secondsUpdated', () => {
      document.title = `NUACOM Softphone | Video call with ${
        isInviter ? toName : fromName
      } | ${timer.current?.getTimeValues().toString()}`;
    });

    setTimeout(() => {
      if (!callObject.current) return;

      const { present } = callObject.current.participantCounts();

      if (present < 2) {
        handleReject();
      }
    }, noAnswerTimeout);
  };

  const startBroadcastChannel = () => {
    broadcastChannel.current = new BroadcastChannel('video_channel');
    broadcastChannel.current.onmessage = handleBroadcastChannelMessages;
  };

  useEffect(() => {
    if (
      !isLocalCameraOn &&
      !isRemoteCameraOn &&
      !isLocalScreenOn &&
      !isRemoteScreenOn
    ) {
      displayMessage(t('screens.video.terminatioNoCamera'));
      cameraTimer.current = setTimeout(() => {
        handleHangup();
      }, disableCamerasTimeout);
    } else {
      clearTimeout(cameraTimer.current);
      setOpenMessage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocalCameraOn, isRemoteCameraOn, isLocalScreenOn, isRemoteScreenOn]);

  useEffect(() => {
    if (!broadcastChannel.current) {
      startBroadcastChannel();
      startCall();
      startTimers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <audio ref={audioPlayer} src={exitTone} />
      {isConnected ? (
        <BetaMessage>
          <span>BETA</span>
        </BetaMessage>
      ) : null}
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        TransitionComponent={Slide}
        open={openMessage}>
        <GenericMessage>
          <span>{genericMessage}</span>
        </GenericMessage>
      </Snackbar>
      <ScreenShareSelector
        open={openScreenSelector}
        handleClose={() => setOpenScreenSelector(false)}
        handleScreenShareSelection={handleScreenShareSelection}
        devices={screenDevices}
      />
    </>
  );
}
